import { useTranslation } from 'react-i18next'
import { Undo2 } from 'lucide-react'
import type { MatchDetail } from '@brochure/shared'
import { formatClock } from '@/lib/match-clock'
import { cn } from '@/lib/utils'

export type EventKind = 'cup' | 'rearrangement' | 'airball'

type EventLogProps = {
  detail: MatchDetail
  /** Se presente, mostra il pulsante Annulla su ogni voce (cruscotto arbitro); assente = sola lettura. */
  onUndo?: (kind: EventKind, id: string) => void
}

type LogEntry = { id: string; second: number; kind: EventKind; label: string; chip?: string; side: 'home' | 'away' | null }

const DOT = { home: 'bg-sea', away: 'bg-coral' }

/** Registro unico di bicchieri, pareggi, spostamenti e airball, il più recente in cima. Il pallino dice la squadra
 * di chi ha agito (mare in casa, corallo in trasferta); per l'arbitro ogni voce ha "Annulla" da 44px. */
export function EventLog({ detail, onUndo }: EventLogProps) {
  const { t } = useTranslation()
  const { homeTeam, awayTeam } = detail
  const players = [...homeTeam.players, ...awayTeam.players]
  const playerName = (playerId: string) => players.find((p) => p.id === playerId)?.name ?? playerId
  const playerSide = (playerId: string): LogEntry['side'] =>
    homeTeam.players.some((p) => p.id === playerId) ? 'home' : awayTeam.players.some((p) => p.id === playerId) ? 'away' : null
  const teamName = (teamId: string) => (homeTeam.id === teamId ? homeTeam.name : awayTeam.name)

  const entries: LogEntry[] = [
    ...detail.cupEvents.map((event) => ({
      id: event.id,
      second: event.second,
      kind: 'cup' as const,
      label: event.isRedemption
        ? t('stoccoPong.match.log.redemption', { player: playerName(event.scorerId) })
        : t('stoccoPong.match.log.cup', { player: playerName(event.scorerId), cup: event.cupIndex + 1 }),
      chip: event.isRedemption ? t('stoccoPong.match.log.chips.redemption') : undefined,
      side: playerSide(event.scorerId),
    })),
    ...detail.rearrangements.map((rearrangement) => ({
      id: rearrangement.id,
      second: rearrangement.second,
      kind: 'rearrangement' as const,
      label: t('stoccoPong.match.log.rearrangement', { team: teamName(rearrangement.requestingTeamId) }),
      chip: t('stoccoPong.match.log.chips.rearrangement'),
      side: rearrangement.requestingTeamId === homeTeam.id ? ('home' as const) : ('away' as const),
    })),
    ...detail.airballs.map((airball) => ({
      id: airball.id,
      second: airball.second,
      kind: 'airball' as const,
      label: t('stoccoPong.match.log.airball', { player: playerName(airball.playerId) }),
      chip: t('stoccoPong.match.log.chips.airball'),
      side: playerSide(airball.playerId),
    })),
    // `Array.prototype.sort` è stabile (garantito dalla spec): a parità di `second` l'ordine di
    // inserimento sopra (bicchieri, poi spostamenti, poi airball) resta invariato.
  ].sort((a, b) => b.second - a.second)

  if (entries.length === 0) {
    return (
      <p data-testid="log" className="rounded-2xl border border-dashed border-paper/15 px-4 py-8 text-center font-body text-sm text-paper/60">
        {t('stoccoPong.match.log.empty')}
      </p>
    )
  }

  return (
    <ul data-testid="log" className="divide-y divide-paper/10 overflow-hidden rounded-2xl border border-paper/10 bg-paper/[0.03]">
      {entries.map((entry) => (
        <li key={entry.id} className="flex min-h-14 items-center gap-3 px-3.5 py-2 font-body text-sm">
          <span className="w-11 shrink-0 tabular-nums text-paper/50">{formatClock(entry.second)}</span>
          <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', entry.side ? DOT[entry.side] : 'bg-paper/30')} />
          <span className="min-w-0 flex-1">
            {entry.chip && (
              <span className="mr-1.5 inline-block rounded-md bg-gold/15 px-1.5 py-0.5 align-middle font-body text-[0.65rem] font-bold tracking-wider text-gold">
                {entry.chip}
              </span>
            )}
            <span className="text-paper/90">{entry.label}</span>
          </span>
          {onUndo && (
            <button
              type="button"
              onClick={() => onUndo(entry.kind, entry.id)}
              className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full px-3 font-body text-xs font-semibold text-coral transition-colors hover:bg-coral/10"
            >
              <Undo2 className="size-4" aria-hidden="true" />
              {t('stoccoPong.match.log.undo')}
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}
